import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Confirm, EmptyState } from "../components/Shared";
import { fmtFCFA, fmtDate, statusBadgeClass, ROLES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import ExportMenu from "../components/ExportMenu";
import { EditableListSelect } from "../components/EditableListSelect";
import PhoneField from "../components/PhoneField";
import { isValidEmail, isValidPhone } from "../lib/validate";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const empty = { name: "", gender: "Male", profession: "", category: "", staff_category: "", role: "", subcontractor_id: "", age: "", status: "Active", phone: "", email: "", date_joined: "", notes: "", company_ids: [] };

export default function Employees() {
  const { isRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [dupWarn, setDupWarn] = useState(null);
  const [payslipEmp, setPayslipEmp] = useState(null);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const addTask = async () => {
    if (!taskTitle.trim()) { toast.error("Enter a task title."); return; }
    try {
      await api.post("/tasks", { employee_id: viewing.id, title: taskTitle.trim(), due_date: taskDue || null });
      setTaskTitle(""); setTaskDue("");
      qc.invalidateQueries({ queryKey: ["emp-tasks", viewing.id] });
      toast.success("Task assigned");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeTask = async (tid) => {
    try { await api.delete(`/tasks/${tid}`); qc.invalidateQueries({ queryKey: ["emp-tasks", viewing.id] }); }
    catch { toast.error("Could not delete task"); }
  };
  const updateTaskDue = async (tid, due) => {
    try {
      await api.patch(`/tasks/${tid}`, { due_date: due || null });
      qc.invalidateQueries({ queryKey: ["emp-tasks", viewing.id] });
      toast.success(due ? "Due date updated" : "Due date cleared");
    } catch { toast.error("Could not update due date"); }
  };
  const [companyOpen, setCompanyOpen] = useState(false);
  const [pFrom, setPFrom] = useState("");
  const [pTo, setPTo] = useState("");
  const canManage = isRole(ROLES.CEO, ROLES.GM);
  const canCreate = isRole(ROLES.CEO, ROLES.GM, ROLES.ACEO);
  const canDelete = isRole(ROLES.CEO);
  const isCEO = isRole(ROLES.CEO);
  const canFinance = isRole(ROLES.CEO, ROLES.FM);
  const activeCompany = localStorage.getItem("stems_company") || "";

  const qc = useQueryClient();
  const { data: rows = [], isLoading: rowsLoading } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const { data: empTasks = [] } = useQuery({ queryKey: ["emp-tasks", viewing?.id], queryFn: () => api.get(`/tasks?employee_id=${viewing.id}`).then((r) => r.data), enabled: !!viewing });
  const { data: allProjects = [] } = useQuery({ queryKey: ["projects-lite"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const [empProjects, setEmpProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState("");
  useEffect(() => {
    if (viewing) setEmpProjects(allProjects.filter((p) => (p.assigned_employees || []).includes(viewing.id)).map((p) => p.id));
  }, [viewing, allProjects]);
  const toggleProject = (pid) => setEmpProjects((s) => (s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]));
  const saveEmpProjects = async () => {
    try {
      await api.patch(`/employees/${viewing.id}/projects`, { project_ids: empProjects });
      qc.invalidateQueries({ queryKey: ["projects-lite"] });
      toast.success("Projects updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => api.get("/companies").then((r) => r.data) });
  const { data: metaConfig = { professions: [] } } = useQuery({ queryKey: ["meta-config"], queryFn: () => api.get("/meta/config").then((r) => r.data) });
  const professions = metaConfig.professions || [];
  const companyMap = Object.fromEntries(companies.map((c) => [c.id, c.name]));
  const load = () => qc.invalidateQueries();
  const displayRows = isRole(ROLES.CEO) ? rows.filter((e) => (e.category || "").toLowerCase() === "management") : isRole(ROLES.SS) ? rows.filter((e) => (e.category || "").toLowerCase() !== "management") : rows;
  const projCount = {};
  allProjects.forEach((p) => (p.assigned_employees || []).forEach((eid) => { projCount[eid] = (projCount[eid] || 0) + 1; }));
  const shownRows = projectFilter ? displayRows.filter((e) => (allProjects.find((p) => p.id === projectFilter)?.assigned_employees || []).includes(e.id)) : displayRows;
  const profOptions = form.profession && !professions.includes(form.profession) ? [form.profession, ...professions] : professions;

  const openNew = () => { setEditing(null); setAddMode(true); setErrors({}); setForm({ ...empty, company_ids: activeCompany ? [activeCompany] : [] }); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setAddMode(false); setErrors({}); setForm({ ...empty, ...e, age: e.age ?? "", date_joined: (e.date_joined || "").slice(0, 10), company_ids: e.company_ids || (e.company_id ? [e.company_id] : []) }); setOpen(true); };

  const toggleCompany = (id) => setForm((f) => ({ ...f, company_ids: f.company_ids.includes(id) ? f.company_ids.filter((c) => c !== id) : [...f.company_ids, id] }));

  const openPayslip = (e) => { setPayslipEmp(e); setPFrom(""); setPTo(""); };
  const payslipThisMonth = () => { const d = new Date(); const y = d.getFullYear(); const mo = d.getMonth() + 1; const last = new Date(y, mo, 0).getDate(); const m = `${y}-${String(mo).padStart(2, "0")}`; setPFrom(`${m}-01`); setPTo(`${m}-${String(last).padStart(2, "0")}`); };
  const fmtD = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const payslipLastMonth = () => { const d = new Date(); const first = new Date(d.getFullYear(), d.getMonth() - 1, 1); const last = new Date(d.getFullYear(), d.getMonth(), 0); setPFrom(fmtD(first)); setPTo(fmtD(last)); };
  const payslipThisYear = () => { const y = new Date().getFullYear(); setPFrom(`${y}-01-01`); setPTo(`${y}-12-31`); };
  const openCompanyPayslips = () => { setCompanyOpen(true); setPFrom(""); setPTo(""); };
  const downloadCompanyPayslips = () => {
    const qs = new URLSearchParams();
    if (pFrom) qs.set("date_from", pFrom);
    if (pTo) qs.set("date_to", pTo);
    const q = qs.toString();
    downloadExport(`/export/employees/payslips.pdf${q ? `?${q}` : ""}`, "company_payment_report.pdf");
    setCompanyOpen(false);
  };
  const downloadPayslip = () => {
    const qs = new URLSearchParams();
    if (pFrom) qs.set("date_from", pFrom);
    if (pTo) qs.set("date_to", pTo);
    const q = qs.toString();
    downloadExport(`/export/employee/${payslipEmp.id}/pdf${q ? `?${q}` : ""}`, `payslip_${payslipEmp.name}.pdf`);
    setPayslipEmp(null);
  };

  const scNorm = form.staff_category || "";
  const isSubCat = /^\s*sub[-\s]?contractor\s*$/i.test(scNorm);
  const isSubEmp = /^\s*sub[-\s]?employee\s*$/i.test(scNorm);
  const hideDesignation = isSubCat || isSubEmp;
  const showSubContractor = isSubEmp;

  const save = async () => {
    const errs = {};
    if (form.email && !isValidEmail(form.email)) errs.email = "Enter a valid email address.";
    if (form.phone && !isValidPhone(form.phone)) errs.phone = "Enter a valid phone number.";
    if (isSubEmp && !form.subcontractor_id) errs.subcontractor_id = "Select the sub contractor this sub-employee works under.";
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Please fix the highlighted fields."); return; }
    setErrors({});
    const payload = { ...form, age: form.age ? Number(form.age) : null };
    if (!editing && (form.email || form.phone)) {
      try {
        const { data } = await api.get("/employees/duplicate-check", { params: { email: form.email || "", phone: form.phone || "" } });
        if (data.matches && data.matches.length) { setDupWarn({ matches: data.matches, payload }); return; }
      } catch { /* ignore duplicate-check failures, proceed to save */ }
    }
    await doSave(payload);
  };
  const doSave = async (payload) => {
    try {
      let resp = null;
      if (editing) { await api.put(`/employees/${editing.id}`, payload); }
      else { resp = (await api.post("/employees", payload)).data; }
      if (resp?.pending_approval) toast.info(resp.message || "Submitted to the CEO for approval");
      else toast.success(editing ? "Employee updated" : "Employee added");
      setOpen(false); setDupWarn(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (e) => {
    try { await api.delete(`/employees/${e.id}`); toast.success("Employee deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const selectMember = (id) => {
    const emp = rows.find((r) => r.id === id);
    if (!emp) return;
    setEditing(emp);
    setForm({ ...empty, ...emp, age: emp.age ?? "", date_joined: (emp.date_joined || "").slice(0, 10), company_ids: emp.company_ids || (emp.company_id ? [emp.company_id] : []) });
  };
  const saveMember = async () => {
    if (!editing) { toast.error("Select a member first."); return; }
    try {
      await api.put(`/employees/${editing.id}`, { ...editing, company_ids: form.company_ids });
      toast.success("Member updated"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={isRole(ROLES.CEO) ? "Administration" : "Employees"} subtitle="Employee records, salaries and status">
        <ExportMenu dataset="employees" filename="stems_employees" testId="employees-export" />
        {canFinance && <Button variant="outline" data-testid="company-payslips-btn" onClick={openCompanyPayslips}><FileText className="h-4 w-4 mr-2" /> Company payslips</Button>}
        {canCreate && <Button data-testid="add-employee-button" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> {isRole(ROLES.CEO) ? "Add Member" : "Add Employee"}</Button>}
      </PageHeader>

      {allProjects.length > 0 && (
        <div className="flex items-center gap-2 mb-4" data-testid="employee-project-filter">
          <span className="text-sm text-slate-500">Filter by project:</span>
          <Select value={projectFilter || "all"} onValueChange={(v) => setProjectFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[240px]" data-testid="project-filter-trigger"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {allProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {rowsLoading ? <TableSkeleton cols={9} /> : displayRows.length === 0 ? <EmptyState text="No employees yet." action={canCreate ? <Button data-testid="empty-new-employee" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#152C6F]">{isRole(ROLES.CEO) ? "Add your first member" : "Add your first employee"}</Button> : null} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead>{!isCEO && <TableHead>Projects</TableHead>}<TableHead>Profession</TableHead><TableHead>Designation</TableHead><TableHead>Category</TableHead>{isCEO && <TableHead>Role</TableHead>}<TableHead>Gender</TableHead>
              <TableHead>Phone</TableHead><TableHead className="text-right">Salary</TableHead>
              <TableHead>Status</TableHead>{companies.length > 1 && <TableHead>Units</TableHead>}<TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shownRows.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.id}`} className="group">
                  <TableCell className="font-medium text-slate-800">{e.name}</TableCell>
                  {!isCEO && (
                  <TableCell>
                    <span data-testid={`emp-project-count-${e.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                      {projCount[e.id] || 0} {(projCount[e.id] || 0) === 1 ? "project" : "projects"}
                    </span>
                  </TableCell>
                  )}
                  <TableCell className="text-slate-600">{e.profession || "—"}</TableCell>
                  <TableCell className="text-slate-600">{e.category || "—"}</TableCell>
                  <TableCell className="text-slate-600">
                    {e.staff_category || "—"}
                    {(e.staff_category || "") === "Subcontractor" && (() => {
                      const subs = rows.filter((r) => r.subcontractor_id === e.id);
                      return subs.length ? (
                        <span data-testid={`emp-subs-${e.id}`} title={subs.map((s) => `${s.name}${s.category ? ` (${s.category})` : ""}${s.phone ? ` · ${s.phone}` : ""}`).join("\n")}
                          className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-100 cursor-help">
                          {subs.length} sub-employee{subs.length === 1 ? "" : "s"}
                        </span>
                      ) : null;
                    })()}
                  </TableCell>
                  {isCEO && <TableCell className="text-slate-600" data-testid={`emp-role-${e.id}`}>{e.role || "—"}</TableCell>}
                  <TableCell className="text-slate-600">{e.gender || "—"}</TableCell>
                  <TableCell className="text-slate-600">{e.phone || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num" data-testid={`emp-salary-${e.id}`}>{fmtFCFA(isCEO ? (e.salary_total ?? e.salary) : e.salary)}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(e.status)}`}>{e.status}</span></TableCell>
                  {companies.length > 1 && (
                    <TableCell>
                      <div className="flex flex-wrap gap-1" data-testid={`employee-units-${e.id}`}>
                        {(e.company_ids || (e.company_id ? [e.company_id] : [])).map((cid) => (
                          <span key={cid} className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap">{companyMap[cid] || "—"}</span>
                        ))}
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <div data-testid={`emp-actions-${e.id}`} className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white shadow-md px-1 py-0.5 opacity-100 transition-opacity md:focus-within:opacity-100">
                        <Button size="icon" variant="ghost" data-testid={`view-employee-${e.id}`} title="View details" onClick={() => setViewing(e)}><Eye className="h-4 w-4" /></Button>
                        {canFinance && <Button size="icon" variant="ghost" data-testid={`payslip-${e.id}`} title="Download payment report" onClick={() => openPayslip(e)}><FileText className="h-4 w-4" /></Button>}
                        {canManage && !isRole(ROLES.CEO) && <Button size="icon" variant="ghost" data-testid={`edit-employee-${e.id}`} title="Edit" onClick={() => openEdit(e)}><Pencil className="h-4 w-4" /></Button>}
                        {canDelete && <Confirm onConfirm={() => remove(e)} title="Delete employee?" description={`This removes "${e.name}". Consider setting status to Inactive to preserve payment history.`}
                          trigger={<Button size="icon" variant="ghost" data-testid={`delete-employee-${e.id}`} title="Delete" className="text-red-600"><Trash2 className="h-4 w-4" /></Button>} />}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{isRole(ROLES.CEO) ? (addMode ? "Add Member" : "Edit Member") : (editing ? "Edit Employee" : "Add Employee")}</DialogTitle></DialogHeader>
          {isRole(ROLES.CEO) ? (
            <div className="space-y-4">
              <F label="Full Name">
                <Select value={editing?.id || ""} onValueChange={selectMember}>
                  <SelectTrigger data-testid="member-name-select"><SelectValue placeholder="Select a member" /></SelectTrigger>
                  <SelectContent>
                    {displayRows.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              {editing && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-1" data-testid="member-details">
                  <VRow label="Profession">{editing.profession || "—"}</VRow>
                  <VRow label="Role"><span data-testid="member-role">{editing.role || "—"}</span></VRow>
                  <VRow label="Designation">{editing.category || "—"}</VRow>
                  <VRow label="Category">{editing.staff_category || "—"}</VRow>
                  <VRow label="Gender">{editing.gender || "—"}</VRow>
                  <VRow label="Salary">{(editing.staff_category || "") === "Subcontractor" ? <span className="text-purple-700 font-semibold">Subcontractor</span> : fmtFCFA(editing.salary_total ?? editing.salary)}</VRow>
                  <VRow label="Status">{editing.status || "—"}</VRow>
                  <VRow label="Phone">{editing.phone || "—"}</VRow>
                  <VRow label="Email">{editing.email || "—"}</VRow>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm">Business Units</Label>
                <p className="text-xs text-slate-400 -mt-1">Select every unit this member belongs to.</p>
                {companies.length === 0 ? <p className="text-sm text-slate-400">No business units defined.</p> : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="member-companies">
                    {companies.map((c) => (
                      <label key={c.id} data-testid={`member-company-${c.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={form.company_ids.includes(c.id)} onChange={() => toggleCompany(c.id)} className="h-4 w-4 accent-[#1E3A8A]" />
                        <span className="text-slate-700">{c.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Full Name"><Input data-testid="employee-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Profession">
              <Select value={form.profession || ""} onValueChange={(v) => setForm({ ...form, profession: v })}>
                <SelectTrigger data-testid="employee-profession"><SelectValue placeholder="Select profession" /></SelectTrigger>
                <SelectContent>
                  {profOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Category">
              <EditableListSelect value={form.staff_category || ""} onValueChange={(v) => setForm({ ...form, staff_category: v })}
                endpoint="/staff-categories" noun="category" canManage={isRole(ROLES.CEO)}
                placeholder="Select category" testPrefix="employee-staffcat" />
            </F>
            {!hideDesignation && (
              <F label="Designation">
                <EditableListSelect value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}
                  endpoint="/employee-categories" noun="designation" canManage={isRole(ROLES.CEO)}
                  placeholder="Select designation" testPrefix="employee-category" />
              </F>
            )}
            <F label="Role">
              <EditableListSelect value={form.role || ""} onValueChange={(v) => setForm({ ...form, role: v })}
                endpoint="/employee-roles" noun="role" canManage={isRole(ROLES.CEO)}
                placeholder="Select role" testPrefix="employee-role" />
            </F>
            {showSubContractor && (
              <F label="Sub Contractor">
                <Select value={form.subcontractor_id || ""} onValueChange={(v) => setForm({ ...form, subcontractor_id: v })}>
                  <SelectTrigger data-testid="employee-subcontractor" className={errors.subcontractor_id ? "border-red-400" : ""}><SelectValue placeholder="Select sub contractor" /></SelectTrigger>
                  <SelectContent>
                    {rows.filter((r) => /^\s*sub[-\s]?contractor\s*$/i.test(r.staff_category || "") && r.id !== editing?.id).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.subcontractor_id && <p className="text-xs text-red-600" data-testid="employee-subcontractor-error">{errors.subcontractor_id}</p>}
              </F>
            )}
            <F label="Gender">
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="employee-gender"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Age"><Input data-testid="employee-age" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} /></F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="employee-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem><SelectItem value="On Leave">On Leave</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Phone">
              <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="employee-phone" invalid={!!errors.phone} />
              {errors.phone && <p className="text-xs text-red-600" data-testid="employee-phone-error">{errors.phone}</p>}
            </F>
            <F label="Email">
              <Input data-testid="employee-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={errors.email ? "border-red-400" : ""} />
              {errors.email && <p className="text-xs text-red-600" data-testid="employee-email-error">{errors.email}</p>}
            </F>
            <F label="Date Joined"><Input data-testid="employee-joined" type="date" value={form.date_joined} onChange={(e) => setForm({ ...form, date_joined: e.target.value })} /></F>
          </div>
          <div className="space-y-2 mt-1">
            <Label className="text-sm">Business Units</Label>
            <p className="text-xs text-slate-400 -mt-1">Select every unit this employee belongs to.</p>
            {companies.length === 0 ? <p className="text-sm text-slate-400">No business units defined.</p> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="employee-companies">
                {companies.map((c) => (
                  <label key={c.id} data-testid={`employee-company-${c.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={form.company_ids.includes(c.id)} onChange={() => toggleCompany(c.id)} className="h-4 w-4 accent-[#1E3A8A]" />
                    <span className="text-slate-700">{c.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            {isRole(ROLES.CEO)
              ? <Button data-testid="employee-form-submit" onClick={saveMember} disabled={!editing} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{addMode ? "Add Member" : "Save Changes"}</Button>
              : <Button data-testid="employee-form-submit" onClick={save} disabled={!form.name} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{editing ? "Save Changes" : "Add Employee"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="view-employee-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{viewing?.name}
            {viewing && <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(viewing.status)}`}>{viewing.status}</span>}
          </DialogTitle></DialogHeader>
          {viewing && (
            <div className="mt-1">
              <VRow label="Profession">{viewing.profession || "—"}</VRow>
              <VRow label="Designation">{viewing.category || "—"}</VRow>
              <VRow label="Category">{viewing.staff_category || "—"}</VRow>
              <VRow label="Gender">{viewing.gender || "—"}</VRow>
              <VRow label="Age">{viewing.age ?? "—"}</VRow>
              <VRow label="Salary">{fmtFCFA(isCEO ? (viewing.salary_total ?? viewing.salary) : viewing.salary)}</VRow>
              <VRow label="Phone">{viewing.phone || "—"}</VRow>
              <VRow label="Email">{viewing.email || "—"}</VRow>
              <VRow label="Date Joined">{viewing.date_joined ? fmtDate(viewing.date_joined) : "—"}</VRow>
              <VRow label="Business Units">
                <div className="flex flex-wrap gap-1 justify-end">
                  {(viewing.company_ids || (viewing.company_id ? [viewing.company_id] : [])).map((cid) => (
                    <span key={cid} className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100">{companyMap[cid] || "—"}</span>
                  ))}
                </div>
              </VRow>
              {viewing.notes && <VRow label="Notes">{viewing.notes}</VRow>}
              <div className="mt-4 pt-4 border-t border-slate-100" data-testid="employee-tasks-panel">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Tasks</div>
                {empTasks.length === 0 ? <p className="text-sm text-slate-400 mb-3" data-testid="employee-tasks-empty">No tasks assigned.</p> : (
                  <div className="space-y-1.5 mb-3">
                    {empTasks.map((t) => (
                      <div key={t.id} data-testid={`emp-task-${t.id}`} className="flex items-center justify-between gap-2 text-sm">
                        <span className={t.status === "done" ? "text-slate-400 line-through" : "text-slate-700"}>{t.title}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <Input type="date" value={t.due_date ? t.due_date.slice(0, 10) : ""} onChange={(e) => updateTaskDue(t.id, e.target.value)}
                            className="h-7 w-[140px] text-xs" data-testid={`emp-task-due-${t.id}`} title="Set or edit due date" />
                          <span className={`text-xs font-semibold ${t.status === "done" ? "text-emerald-600" : "text-amber-600"}`}>{t.status === "done" ? "Done" : "Open"}</span>
                          <button onClick={() => removeTask(t.id)} data-testid={`emp-task-delete-${t.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="New task…" className="flex-1 min-w-[160px]" data-testid="employee-task-title" />
                  <Input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="w-[150px]" data-testid="employee-task-due" />
                  <Button size="sm" onClick={addTask} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="employee-task-add"><Plus className="h-4 w-4 mr-1" /> Assign</Button>
                </div>
              </div>
              {canManage && (
                <div className="mt-4 pt-4 border-t border-slate-100" data-testid="employee-projects-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wide text-slate-400">Projects</div>
                    <Button size="sm" variant="outline" onClick={saveEmpProjects} data-testid="employee-projects-save">Save projects</Button>
                  </div>
                  {allProjects.length === 0 ? <p className="text-sm text-slate-400">No projects available.</p> : (
                    <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto" data-testid="employee-projects-list">
                      {allProjects.map((pr) => (
                        <label key={pr.id} data-testid={`emp-proj-${pr.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                          <input type="checkbox" checked={empProjects.includes(pr.id)} onChange={() => toggleProject(pr.id)} className="h-4 w-4 accent-[#1E3A8A]" />
                          <span className="text-slate-700 truncate">{pr.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-2">An employee can be assigned to multiple projects.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
            {canManage && !isRole(ROLES.CEO) && <Button data-testid="view-to-edit-btn" onClick={() => { const e = viewing; setViewing(null); openEdit(e); }} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Pencil className="h-4 w-4 mr-2" /> Edit</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!dupWarn} onOpenChange={(o) => !o && setDupWarn(null)}>
        <AlertDialogContent data-testid="employee-duplicate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Possible duplicate employee</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {dupWarn?.matches?.map((m) => (
                  <span key={m.id} className="block">“{m.name}” already uses the same {m.fields.join(" and ")}.</span>
                ))}
                <span className="block mt-2">Do you still want to add this employee?</span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="duplicate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="duplicate-confirm" onClick={() => doSave(dupWarn.payload)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Add anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!payslipEmp} onOpenChange={(o) => !o && setPayslipEmp(null)}>
        <DialogContent data-testid="payslip-dialog">
          <DialogHeader><DialogTitle>Payment report · {payslipEmp?.name}</DialogTitle>
            <DialogDescription>Choose an optional month or date range, or leave both blank for all-time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Choose a period, or leave both blank for all-time.</p>
            <div className="grid grid-cols-2 gap-3">
              <F label="From"><Input type="date" value={pFrom} onChange={(e) => setPFrom(e.target.value)} data-testid="payslip-from" /></F>
              <F label="To"><Input type="date" value={pTo} onChange={(e) => setPTo(e.target.value)} data-testid="payslip-to" /></F>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={payslipThisMonth} data-testid="payslip-this-month">This month</Button>
              <Button variant="outline" size="sm" onClick={payslipLastMonth} data-testid="payslip-last-month">Last month</Button>
              <Button variant="outline" size="sm" onClick={payslipThisYear} data-testid="payslip-this-year">This year</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayslipEmp(null)}>Cancel</Button>
            <Button onClick={downloadPayslip} data-testid="payslip-download" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><FileText className="h-4 w-4 mr-2" /> Download</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={companyOpen} onOpenChange={setCompanyOpen}>
        <DialogContent data-testid="company-payslip-dialog">
          <DialogHeader><DialogTitle>Company payment report</DialogTitle>
            <DialogDescription>Combined payment totals for every employee in the current business unit. Pick a period or leave blank for all-time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <F label="From"><Input type="date" value={pFrom} onChange={(e) => setPFrom(e.target.value)} data-testid="company-payslip-from" /></F>
              <F label="To"><Input type="date" value={pTo} onChange={(e) => setPTo(e.target.value)} data-testid="company-payslip-to" /></F>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={payslipThisMonth}>This month</Button>
              <Button variant="outline" size="sm" onClick={payslipLastMonth}>Last month</Button>
              <Button variant="outline" size="sm" onClick={payslipThisYear}>This year</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanyOpen(false)}>Cancel</Button>
            <Button onClick={downloadCompanyPayslips} data-testid="company-payslip-download" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><FileText className="h-4 w-4 mr-2" /> Download</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const VRow = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-sm text-slate-800 text-right font-medium">{children}</span>
  </div>
);
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>;
